import {
  GaugeIcon,
  CreditCardIcon,
  TagIcon,
  ShieldCheckOutlineIcon,
  ArrowRightIcon,
} from "@talladega/icons";
import getDisplayCoverageLength from "@modules/purchase/warranty/helpers/getDisplayCoverageLength";
import { Button, Listbox, SlideToggle } from "@talladega/components";
import {
  formatCurrency,
  formatNumber,
  dateTimeFormatter,
} from "@talladega/helpers";
import { convert, LocalDate } from "@js-joda/core";
import {
  ExtendedWarrantyCoverageDetails,
  ExtendedWarrantyRatesResponse,
  ProductCoverage,
} from "@modules/purchase/warranty/types/ExtendedWarrantyRates";
import useCoverageConfig from "@modules/purchase/warranty/hooks/useCoverageConfig";
import {
  ExtendedWarrantyPremiumAutoCanadaDigital,
  ExtendedWarrantyAmaAutoCanadaDigital,
  getIAProgramById,
} from "@modules/purchase/warranty/types/IAProgram";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "next-i18next";
import WarrantyVehicleInfoCard from "./WarrantyVehicleInfoCard";

type Props = {
  ratesResponse: ExtendedWarrantyRatesResponse;
  rateDiscountValue?: number;
  onQuoteDifferentVehicle: (element: string) => void;
  onSelectCoverage: (
    coverage: ProductCoverage,
    selectedCoverageDetails: ExtendedWarrantyCoverageDetails
  ) => void;
};

const getProgramNameKey = (ratesResponse: ExtendedWarrantyRatesResponse) => {
  const iaProgram = getIAProgramById(ratesResponse.productDetails.programId);
  const coverageAbbr = ratesResponse?.coverages[0]?.type ?? "";

  return (
    iaProgram?.coverageTypes.find((x) => x.abbr === coverageAbbr)?.i18nKey ?? ""
  );
};

const WarrantyCoverageConfigurator = ({
  ratesResponse,
  rateDiscountValue,
  onQuoteDifferentVehicle,
  onSelectCoverage,
}: Props) => {
  const { t } = useTranslation(["purchase", "common"]);
  const locale = useRouter().locale || "en";

  const [paymentFrequency, setPaymentFrequency] = useState<"full" | "monthly">(
    "monthly"
  );

  const [selectedCoverage, setSelectedCoverage] = useState<
    ProductCoverage | undefined
  >(undefined);

  const programName = getProgramNameKey(ratesResponse);

  const handleCoverageChange = (coverage: ProductCoverage) => {
    setSelectedCoverage(coverage);
  };

  const coverageOptions = ratesResponse?.coverages.reduce<ProductCoverage[]>(
    (acc, current) => {
      // Check if the current term and termKm combination already exists in our accumulator
      const exists = acc.find(
        (coverage) =>
          coverage.termKm === current.termKm && coverage.term === current.term
      );

      if (!exists) {
        acc.push(current);
      } else if (current.isHighlightedCoverage) {
        // flag the highlighted coverage
        exists.isHighlightedCoverage = true;
      }

      return acc;
    },
    []
  );

  const isPremiumCoverage =
    ratesResponse?.productDetails.programId ===
    ExtendedWarrantyPremiumAutoCanadaDigital.id;

  const isAmaCoverage =
    ratesResponse?.productDetails.programId ===
    ExtendedWarrantyAmaAutoCanadaDigital.id;

  const coverageConfig = useCoverageConfig(ratesResponse?.coverages, locale);

  const config =
    coverageConfig[selectedCoverage?.type as keyof typeof coverageConfig];

  const formattedTermKms = formatNumber(
    parseInt(selectedCoverage?.termKm ?? "0", 10),
    locale
  );

  useEffect(() => {
    if (ratesResponse?.coverages) {
      const highlightedCoverage = ratesResponse.coverages.find(
        (x) => x.isHighlightedCoverage
      );
      setSelectedCoverage(highlightedCoverage);
    }
  }, [ratesResponse?.coverages]);

  const getOptionItemDisplayText = (item?: ProductCoverage) => {
    if (!item) return "";

    const termKms = parseInt(item.termKm ?? "", 10);

    return getDisplayCoverageLength(
      item.term,
      termKms,
      locale,
      isPremiumCoverage ? item.termExpiryDate : undefined
    );
  };

  const customRenderOption = (item: ProductCoverage) => {
    return (
      <div className="flex justify-between w-full gap-2">
        <div className="flex items-center">
          <span className="pl-2">{getOptionItemDisplayText(item)}</span>
        </div>
        {item.isHighlightedCoverage && (
          <div className="flex leading-none w-24 items-center text-center rounded-3xl py-1 px-1 text-xs bg-secondary-500 border border-secondary text-white shrink-0 grow-0 self-start">
            <span className="text-center w-full">
              {t("purchase:best_value")}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full items-center justify-center text-center lg:items-start lg:justify-start lg:text-left">
      <div
        id="configurator"
        className="flex flex-col p-5 bg-primary rounded-xl items-center"
      >
        <div className="flex flex-col items-center gap-2 mb-7">
          <div className="rounded-full p-3 bg-gray-50">
            <ShieldCheckOutlineIcon className="w-6 h-6 text-secondary-700" />
          </div>
          {programName !== "" && (
            <p className="text-2xl font-bold">{t(programName)}</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col items-center justify-center gap-5">
            <SlideToggle
              leftLabel={t("common:monthly")}
              leftValue="monthly"
              rightLabel={t("common:full_amount")}
              rightValue="full"
              value={paymentFrequency}
              onChange={(selected) => {
                setPaymentFrequency(selected);
              }}
              rounded="lg"
            />
            <div className="flex items-center justify-center gap-4 lg:gap-7 px-4">
              <div className="grid grid-rows-2 items-center md:grid-flow-col md:items-start md:text-start">
                <div className="col-span-2 md:row-span-2 rounded-full bg-secondary p-2 mx-auto md:mr-2">
                  <GaugeIcon className="w-5 h-5 text-gray-700" />
                </div>
                <div className="col-span-2 font-bold">
                  {isAmaCoverage
                    ? t("purchase:unlimited_kms")
                    : t("common:kms_other", {
                        formattedCount: formattedTermKms,
                      })}
                </div>
                {selectedCoverage?.termExpiryDate && (
                  <div className="col-span-2">
                    {t("purchase:expires_date", {
                      date: dateTimeFormatter(
                        convert(
                          LocalDate.parse(
                            selectedCoverage.termExpiryDate.toString()
                          )
                        ).toDate(),
                        locale || "en",
                        "MMM, YYYY"
                      ),
                    })}
                  </div>
                )}
              </div>
              <div
                id="divider-vertical"
                className="relative border-l border-gray-300 h-28 md:h-12 mx-4"
              />
              {!!selectedCoverage?.payment &&
                !!selectedCoverage?.paymentTerm && (
                  <div className="grid grid-rows-2 items-center md:grid-flow-col md:items-start md:text-start">
                    <div className="col-span-2 md:row-span-2 rounded-full bg-secondary p-2 mx-auto md:mr-2">
                      <CreditCardIcon className="w-5 h-5 text-gray-700" />
                    </div>
                    {paymentFrequency === "monthly" ? (
                      <>
                        <div className="col-span-2 font-bold">
                          {t("common:value_per_month", {
                            value: `${formatCurrency(
                              selectedCoverage.payment,
                              locale
                            )}`,
                          })}
                        </div>
                        <div className="col-span-2">
                          {t("common:number_payments", {
                            number: selectedCoverage.paymentTerm,
                          })}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-span-2 font-bold">
                          {formatCurrency(selectedCoverage.price, locale)}
                        </div>
                        <div className="col-span-2">
                          {t("common:full_amount")}
                        </div>
                      </>
                    )}
                  </div>
                )}
            </div>
            {!!rateDiscountValue && (
              <div className="flex gap-3 justify-center items-center rounded-xl py-4 px-2 md:px-6 bg-gray-50 text-xs md:text-normal">
                <TagIcon className="w-5 h-5 text-success-600" />
                <Trans
                  t={t}
                  i18nKey="purchase:this_quote_includes_discount"
                  components={{
                    badge: (
                      <div className="text-success-700 text-xs md:text-sm border border-success-200 rounded-full py-1 px-2 bg-success-50" />
                    ),
                  }}
                  values={{
                    discount: formatCurrency(rateDiscountValue, locale),
                  }}
                />
              </div>
            )}
          </div>
          <div
            id="divider-horizontal"
            className="relative border-b border-gray-300 my-4"
          />
          <div className="flex flex-col items-start justify-start gap-5 mb-3">
            <p className="font-bold text-xl">
              {t("purchase:configure_your_plan")}:
            </p>
            <div className="w-full">
              <Listbox
                items={coverageOptions ?? []}
                label={t("purchase:length_of_coverage")}
                itemsTextDisplay="full"
                keyOption={(item) => `${item.term}-${item.termKm}`}
                displayOption={getOptionItemDisplayText}
                renderOption={customRenderOption}
                displayButton={getOptionItemDisplayText}
                valueOption={(item) => item}
                onChange={handleCoverageChange}
                selectedItem={selectedCoverage || undefined}
              />
            </div>
          </div>
          {ratesResponse.vehicleDisplayDetails && (
            <WarrantyVehicleInfoCard
              vehicleDetails={ratesResponse.vehicleDisplayDetails}
              className="bg-secondary"
              ctaElements={
                <div
                  className="text-xs md:text-sm underline mr-auto px-0 cursor-pointer text-primary-600"
                  onClick={() =>
                    onQuoteDifferentVehicle("Quote-PlanConfigurator")
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onQuoteDifferentVehicle("Quote-PlanConfigurator");
                    }
                  }}
                >
                  {t("purchase:quote_different_vehicle")}
                </div>
              }
            />
          )}

          <Button
            buttonStyle="primary"
            className="w-full mt-6"
            rightIcon={<ArrowRightIcon className="w-6 h-6" />}
            disabled={!selectedCoverage}
            onClick={() =>
              selectedCoverage &&
              onSelectCoverage(selectedCoverage, ratesResponse)
            }
          >
            {t("common:continue")}
          </Button>

          {config?.caption && selectedCoverage && (
            <p className="text-xs mt-3">
              {config.caption({
                paymentTerm: selectedCoverage.paymentTerm,
                termKm: `${formatNumber(
                  parseInt(selectedCoverage.termKm ?? "", 10),
                  locale
                )} KM`,
                deductible: formatCurrency(
                  parseInt(selectedCoverage.deductible ?? "", 10),
                  locale,
                  { showCents: "never" }
                ),
                downPayment:
                  paymentFrequency === "monthly"
                    ? formatCurrency(
                        parseInt(
                          selectedCoverage.downPayment?.toString() ?? "",
                          10
                        ),
                        locale,
                        {
                          showCents: "never",
                        }
                      )
                    : "",
                serviceClaimAmount: selectedCoverage.serviceClaimAmount,
                totalClaimAmount: selectedCoverage.totalClaimAmount,
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarrantyCoverageConfigurator;
